import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { Blocs } from '@business/blocs';
import { Repository } from '@core/utils/repository';
import { showAlert, showDeleteConfirmation, showYesNo } from '@modules/common';
import { EMPTY, of } from 'rxjs';
import { Formatter } from '@core/utils/formatter';
import { switchMap, map } from 'rxjs/operators';
import { R } from '@core/utils/r';
import {
  CalendarTemplateId,
  CalendarTemplateImportId,
  CalendarTemplateImportResponse,
  CalendarTemplateResponse,
  ShiftTemplateImportRequest,
} from '@business/entities/calendar_template';
import { CalendarTemplateAPI } from '@business/api/calendar_template';

export class CalendarTemplateBloc extends BaseBloc {
  private readonly calendarTemplateRepo = new Repository<CalendarTemplateResponse>({
    getItemId: item => item.id,
  });

  readonly CalendarsTemplates$ = this.calendarTemplateRepo.items$.pipe(map(items => R.sortBy(items, item => item.title.toLowerCase())));

  // Import batches of the object currently being viewed (a work order's Schedules tab)
  private readonly templateImportsRepo = new Repository<CalendarTemplateImportResponse>({
    getItemId: item => item.id,
  });

  readonly templateImports$ = this.templateImportsRepo.items$;

  readonly selectTemplateImport = this.templateImportsRepo.selectItem;

  onReset() {
    this.calendarTemplateRepo.reset();
    this.templateImportsRepo.reset();
  }

  fetchTemplateImports = (objectType: string, objectId: string) => {
    return CalendarTemplateAPI.fetchTemplateImports(objectType, objectId).pipe(
      this.templateImportsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_template_imports_error') }),
    );
  };

  /**
   * "Change project start date" for one imported schedule. Dry-runs first so the user can confirm
   * (or cancel) the exact working-day shift and event count; only then applies it.
   */
  shiftTemplateImport = (id: CalendarTemplateImportId, request: ShiftTemplateImportRequest, options?: { skipConfirm?: boolean }) => {
    return CalendarTemplateAPI.shiftTemplateImport(id, request, true).pipe(
      switchMap(preview => {
        if (preview.days === 0) {
          return showAlert('No change', 'The new start date is the same working day as the current one — nothing to move.').pipe(switchMap(() => EMPTY));
        }
        const direction = preview.days > 0 ? 'later' : 'earlier';
        const n = Math.abs(preview.days);
        const confirm$ = options?.skipConfirm
          ? of(true)
          : showYesNo(
              'Shift Schedule',
              `Move all <strong>${preview.eventsCount}</strong> events of <strong>${preview.import.templateTitle ?? 'this schedule'}</strong> ` +
                `<strong>${n} working day${n === 1 ? '' : 's'} ${direction}</strong> ` +
                `(new start ${Formatter.formatTime(preview.startDate)})?<br/><br/>` +
                `Dependencies stay intact and weekends/holidays are skipped.`,
              { btnYesLabel: 'Yes, shift schedule', btnNoLabel: 'Cancel' },
            );
        return confirm$.pipe(switchMap(ok => (ok ? CalendarTemplateAPI.shiftTemplateImport(id, request, false) : EMPTY)));
      }),
      this.templateImportsRepo.ops.upsertOne(res => ({ item: res.import })),
      // The work-order calendar keeps its events in the shared EventBloc repo and only refetches on its
      // own view changes — resync its last-loaded range so the moved events show without a reload
      // (the shift can be triggered from the details card while the Schedules tab is mounted).
      switchMap(res => resolve(Blocs.events).refetchLastRange().pipe(map(() => res))),
      handleMessage({ type: requestMessage('shift_template_import') }),
    );
  };

  deleteTemplateImport = (id: CalendarTemplateImportId) => {
    return showDeleteConfirmation(
      'Delete Imported Schedule',
      'This removes the imported schedule AND all of its calendar events from this work order. Continue?',
    ).pipe(
      switchMap(() => CalendarTemplateAPI.deleteTemplateImport(id)),
      this.templateImportsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_template_import'),
      }),
    );
  };

  /** Hand-made events (no schedule import) on one calendar: count + date range for the Schedules tab. */
  fetchManualEvents = (objectType: string, objectId: string) => {
    return CalendarTemplateAPI.fetchManualEvents(objectType, objectId).pipe(handleMessage({ error: requestMessage('fetch_manual_events_error') }));
  };

  /** Delete every hand-made event on one calendar (imported schedules and invoice events are not touched). */
  deleteManualEvents = (objectType: string, objectId: string, count: number) => {
    return showDeleteConfirmation(
      'Delete Manual Events',
      `This deletes all ${count} manual event${count === 1 ? '' : 's'} on this calendar. Imported schedules are not affected. Continue?`,
    ).pipe(
      switchMap(() => CalendarTemplateAPI.deleteManualEvents(objectType, objectId)),
      handleMessage({ type: requestMessage('delete_manual_events') }),
    );
  };

  fetchCalendarTemplates = () => {
    return CalendarTemplateAPI.fetchCalendarTemplates().pipe(
      this.calendarTemplateRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_calendars_template_error') }),
    );
  };

  createCalendarTemplate = (title: string) => {
    return CalendarTemplateAPI.createCalendarTemplate(title).pipe(
      this.calendarTemplateRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_calendar_template'),
      }),
    );
  };

  updateCalendarTemplate = (id: CalendarTemplateId, title: string) => {
    return CalendarTemplateAPI.updateCalendarTemplate(id, title).pipe(
      this.calendarTemplateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_calendar_template'),
      }),
    );
  };

  duplicateCalendarTemplate = (id: CalendarTemplateId, title: string) => {
    return CalendarTemplateAPI.duplicateCalendarTemplate(id, title).pipe(
      this.calendarTemplateRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('duplicate_calendar_template'),
      }),
    );
  };

  deleteCalendarTemplate = (id: CalendarTemplateId) => {
    return showDeleteConfirmation('Delete Calendar Template', 'Do you really want to remove this calendar template?').pipe(
      switchMap(() => CalendarTemplateAPI.deleteCalendarTemplate(id)),
      this.calendarTemplateRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_calendar_template'),
      }),
    );
  };

  previewCalendarTemplate = (id: CalendarTemplateId, startDate: number) =>
    CalendarTemplateAPI.previewCalendarTemplate(id, startDate).pipe(
      handleMessage({ error: requestMessage('preview_calendar_template_error') }),
    );

  validateTemplate = (scheduleId: CalendarTemplateId, orderId: string) =>
    CalendarTemplateAPI.validateCalendarTemplate(scheduleId, orderId).pipe(
      handleMessage({ error: requestMessage('validate_schedule_template_error') }),
    );
}
